import { createContext, useContext, useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';

const BrandingContext = createContext({ branding: null });
export const useBranding = () => useContext(BrandingContext);

const hexToHsl = (hex) => {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const applyBranding = (b) => {
  const root = document.documentElement;
  if (b?.branding_enabled) {
    const p = hexToHsl(b.primary_color);
    if (p) {
      root.style.setProperty('--primary', p);
      root.style.setProperty('--ring', p);
    }
    if (b.favicon_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = b.favicon_url;
    }
    if (b.public_name) document.title = b.public_name;
  } else {
    root.style.removeProperty('--primary');
    root.style.removeProperty('--ring');
  }
};

export const BrandingProvider = ({ children }) => {
  const { user } = useAuth();
  const { tenant, loading: tenantLoading } = useTenant();
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    // Wait for tenant bootstrap to finish
    if (tenantLoading) return;

    // TENANT-FIRST: if we're on a tenant subdomain (Cloudflare-resolved
    // or Host-based), the tenant's branding is the source of truth.
    // NEVER let a logged-in user's own establishment_id override it —
    // that was the root cause of "entered UCM URL, saw Curicó branding":
    // a super_admin logged into another tenant carried their user.establishment_id
    // to the tenant subdomain and the old BrandingContext used it.
    if (tenant && tenant.type === 'establishment') {
      const t = tenant.branding || {};
      const merged = {
        branding_enabled: !!tenant.branding,
        establishment_id: tenant.establishment?.id || null,
        establishment_nombre: tenant.establishment?.name || null,
        public_name: t.public_name || null,
        welcome_message: t.welcome_message || null,
        primary_color: t.primary_color || null,
        secondary_color: t.secondary_color || null,
        accent_color: t.accent_color || null,
        logo_url: t.logo_url || null,
        favicon_url: t.favicon_url || null,
        login_background_url: t.login_background_url || null,
        powered_by_simuclinic: tenant.powered_by_simuclinic !== false,
      };
      setBranding(merged);
      applyBranding(merged);
      return;
    }

    // GLOBAL MODE (or tenant not found/inactive): fall back to per-user branding
    // for logged-in users, or to a null branding for the global site.
    const params = new URLSearchParams();
    params.set('host', window.location.hostname);
    const estParam = new URLSearchParams(window.location.search).get('est');
    if (user?.establishment_id) params.set('establishment_id', user.establishment_id);
    else if (estParam) params.set('establishment_id', estParam);
    api.get(`/public/branding?${params.toString()}`)
      .then((r) => { setBranding(r.data); applyBranding(r.data); })
      .catch(() => setBranding({ branding_enabled: false }));
  }, [user?.establishment_id, tenant, tenantLoading]);

  return (
    <BrandingContext.Provider value={{ branding }}>
      {children}
    </BrandingContext.Provider>
  );
};
